import { useMemo, useState, useEffect, useRef } from 'react'
import { Box, Flex, Text, useT } from '@minetrace/ui'
import { useFormColors } from '@minetrace/ui/forms'
import { toaster } from '@minetrace/ui'
import { MINING_RIGHT_STATUSES } from '../data/enums'
import type { MiningRightResponse, MiningRightComponentResponseItem } from '../api/models/response/proveedor'
import type { RestrictionCheckSummary } from '../api/models/response/restrictionCheck'
import ubigeoData from '../data/ubigeo.json'
import { Modal } from '@minetrace/ui'
import { getUtmZoneFromGeoJson, isValidLatLng } from '../utils/utmConverter'
import GeoreferenceModal from './GeoreferenceModal'
import ComponentsModal from './ComponentsModal'
import RestrictionWarningCard from './RestrictionWarningCard'
import IngemmetOfficialPanel from './IngemmetOfficialPanel'
import { proveedoresService } from '../api/services/proveedores.service'
import { KMap, FitBounds, useMap } from '../lib/googleMaps'

type UbigeoJson = Record<string, {
  name: string
  provinces: Record<string, {
    name: string
    districts: Record<string, { name: string }>
  }>
}>

const data = ubigeoData as UbigeoJson

const resolveUbigeoNames = (code: string | undefined | null): string[] => {
  if (!code || code.length < 2) return []
  const dept = code.substring(0, 2)
  const deptName = data[dept]?.name
  if (!deptName) return []
  if (code.length < 4) return [deptName]
  const prov = code.substring(0, 4)
  const provName = data[dept]?.provinces[prov]?.name
  if (!provName) return [deptName]
  if (code.length < 6) return [deptName, provName]
  const distName = data[dept]?.provinces[prov]?.districts[code]?.name
  if (!distName) return [deptName, provName]
  return [deptName, provName, distName]
}

type LL = google.maps.LatLngLiteral

const parseGeoJsonPositions = (geoJson: string | null): LL[] => {
  if (!geoJson) return []
  try {
    const geo = JSON.parse(geoJson)
    const coords: number[][] = geo.type === 'Polygon' ? geo.coordinates[0] : []
    return coords
      .map((c) => ({ lat: c?.[1], lng: c?.[0] }) as LL)
      // Validamos el RANGO WGS84, no solo `Number.isFinite`: una coordenada
      // finita fuera de rango cuelga el mapa. Ver isValidLatLng en utmConverter.
      .filter(({ lat, lng }) => isValidLatLng(lat, lng))
  } catch {
    return []
  }
}

/**
 * Polígono imperativo sobre `google.maps.Polygon` (la base de googleMaps no
 * expone Polygon, solo Polyline). Mismo patrón de ciclo de vida que
 * `lib/googleMaps/Polyline.tsx`.
 */
/**
 * Dibuja un poligono sobre el mapa.
 *
 * Se pintan dos capas distintas y no una: el contorno del DERECHO MINERO que publica
 * INGEMMET (el edificio) y, encima, el poligono de la CONCESION que trabaja este minero
 * (su departamento). Hasta sep 2026 eran el mismo dato y el mapa mostraba una sola forma.
 */
const PolygonOverlay = ({
  paths,
  fillOpacity,
  color = '#1d6664',
  strokeWeight = 2,
  zIndex = 2,
}: {
  paths: LL[]
  fillOpacity: number
  color?: string
  strokeWeight?: number
  zIndex?: number
}) => {
  const map = useMap()
  const polyRef = useRef<google.maps.Polygon | null>(null)

  useEffect(() => {
    if (!map) return
    const poly = new google.maps.Polygon()
    poly.setMap(map)
    polyRef.current = poly
    return () => {
      poly.setMap(null)
      polyRef.current = null
    }
  }, [map])

  useEffect(() => {
    polyRef.current?.setOptions({
      paths,
      strokeColor: color,
      strokeWeight,
      fillColor: color,
      fillOpacity,
      clickable: false,
      zIndex,
    })
  }, [paths, fillOpacity, color, strokeWeight, zIndex])

  return null
}

/** Color del contorno del derecho minero. Ambar para que no se confunda con la concesion. */
const COLOR_DERECHO = '#d49a2a'

interface MiningRightCardProps {
  right: MiningRightResponse
  // companyId is required to query the restriction-check endpoint, which is
  // scoped to the proveedor (/proveedores/{companyId}/...). When absent the
  // card simply skips the screening panel; the editor still works.
  companyId?: string | null
  onUpdated?: () => void
  onSavePolygon: (miningRightId: string, vertices: { northing: number; easting: number }[], utmZone: number) => Promise<void>
  onAddComponent: (miningRightId: string, data: { componentTypeId: string; name?: string; northing: number; easting: number }) => Promise<MiningRightComponentResponseItem>
  onRemoveComponent: (miningRightId: string, componentId: string) => Promise<void>
  components: MiningRightComponentResponseItem[]
}

const MiningRightCard = ({ right, companyId, onUpdated, onSavePolygon, onAddComponent, onRemoveComponent, components }: MiningRightCardProps) => {
  const c = useFormColors()
  const { t } = useT()
  const [georefOpen, setGeorefOpen] = useState(false)
  const [componentsOpen, setComponentsOpen] = useState(false)
  const [mapOpen, setMapOpen] = useState(false)
  // La zona UTM se deriva de la geometría guardada (fuente de verdad), igual que
  // el backend. Antes se inicializaba por ubigeo y caía a 18, descuadrando los
  // componentes respecto al polígono.
  const utmZone = useMemo(() => right.utmZone ?? getUtmZoneFromGeoJson(right.geoJson, right.ubigeoCode), [right.utmZone, right.geoJson, right.ubigeoCode])
  const [restrictionCheck, setRestrictionCheck] = useState<RestrictionCheckSummary | null>(null)
  const [rechecking, setRechecking] = useState(false)
  const [importingIngemmet, setImportingIngemmet] = useState(false)
  const [progresoExpediente, setProgresoExpediente] = useState<{ descargadas: number; total: number } | null>(null)

  // Trae el expediente digitalizado empujando lotes hasta terminar.
  //
  // Son ~200 peticiones a INGEMMET (una imagen por pagina), asi que el backend las procesa
  // de a 40 y devuelve el progreso: cada llamada es corta y no retiene la instancia de
  // Cloud Run. El progreso vive en la base, asi que cerrar la pantalla no pierde lo bajado;
  // volver a entrar y pedirlo continua donde quedo.
  const handleDescargarExpediente = async () => {
    if (!companyId || progresoExpediente) return
    setProgresoExpediente({ descargadas: 0, total: right.ingemmetArchivePageCount ?? 0 })
    try {
      // Tope defensivo: con lotes de 40, 50 vueltas cubren 2000 paginas. Si algo dejara de
      // avanzar, corta en vez de quedarse girando para siempre.
      for (let i = 0; i < 50; i++) {
        const est = await proveedoresService.advanceIngemmetExpediente(companyId, right.id)
        setProgresoExpediente({ descargadas: est.downloadedPages, total: est.totalPages })

        if (est.status === 4) {
          toaster.success({ description: 'Expediente descargado y armado como PDF' })
          onUpdated?.()
          return
        }
        if (est.status === 5) {
          toaster.error({ description: est.message ?? 'No se pudo descargar el expediente' })
          return
        }
      }
      toaster.error({ description: 'La descarga no termino. Volve a intentarlo: continua donde quedo.' })
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'No se pudo descargar el expediente' })
    } finally {
      setProgresoExpediente(null)
    }
  }

  // Trae los datos oficiales de INGEMMET. No pisa el poligono existente: si el derecho ya
  // tiene uno dibujado, el backend lo conserva y lo informa, y reemplazarlo es una decision
  // aparte del usuario.
  const handleIngemmetImport = async () => {
    if (!companyId || importingIngemmet) return
    setImportingIngemmet(true)
    try {
      const res = await proveedoresService.importFromIngemmet(companyId, right.id)
      // El poligono que trae INGEMMET es el del DERECHO MINERO completo y se guarda como
      // tal. La concesion del minero -- el area que el trabaja dentro de ese derecho --
      // no se toca: son cosas distintas y copiarlas era el error que se corrigio.
      if (!res.found) {
        toaster.error({ description: res.message ?? 'El código no existe en INGEMMET' })
      } else if (res.boundaryImported) {
        toaster.success({ description: 'Datos importados. El contorno del derecho minero se copió también como concesión.' })
      } else if (res.officialBoundaryImported) {
        toaster.success({ description: 'Datos y polígono del derecho minero importados de INGEMMET.' })
      } else {
        toaster.success({ description: 'Datos oficiales importados de INGEMMET.' })
      }
      onUpdated?.()
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'No se pudo consultar INGEMMET' })
    } finally {
      setImportingIngemmet(false)
    }
  }

  // Re-ejecuta el screening a pedido. Hace falta porque el chequeo es un snapshot
  // atado a una geometria y a un catalogo, y ambos se mueven: sin esto, un analisis
  // desactualizado solo se arregla volviendo a guardar el poligono.
  const handleRecheck = async () => {
    if (!companyId || rechecking) return
    setRechecking(true)
    try {
      const check = await proveedoresService.recheckRestrictions(companyId, right.id)
      setRestrictionCheck(check)
    } catch {
      // Se deja el resultado anterior: perder el analisis vigente porque el
      // re-chequeo fallo dejaria la ficha peor de lo que estaba.
    } finally {
      setRechecking(false)
    }
  }

  // Carga el último screening al montar y cada vez que cambia el polígono.
  // Política acordada: warning informativo, no bloquea el flujo.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!companyId || !right.hasBoundary) {
        if (!cancelled) setRestrictionCheck(null)
        return
      }
      try {
        const check = await proveedoresService.getRestrictionCheck(companyId, right.id)
        if (!cancelled) setRestrictionCheck(check)
      } catch {
        if (!cancelled) setRestrictionCheck(null)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [companyId, right.id, right.hasBoundary, right.geoJson])

  const statusLabel = MINING_RIGHT_STATUSES.find((s) => s.value === right.miningRightStatusId)?.label ?? t('shared.miningRightCard.statusUnknown')
  const isVigente = right.miningRightStatusId === 1
  const isSuspendido = right.miningRightStatusId === 4
  const statusBorderColor = isVigente ? '#1d6664' : isSuspendido ? '#a8740e' : '#b5aa9d'
  const statusTextColor = isVigente ? '#1d6664' : isSuspendido ? '#a8740e' : '#4a5568'

  // Estado de formalización por derecho minero: REINFO (EnProceso=1) = "en vías de
  // formalización" (ámbar); minero formal (Formalizado 2/3) = "formalizado" (verde).
  const isFormalized = right.formalizationStatus === 2 || right.formalizationStatus === 3
  const isInProcess = right.formalizationStatus === 1
  const formalizationLabel = isFormalized
    ? t('shared.miningRightCard.formalized')
    : isInProcess ? t('shared.miningRightCard.inFormalization') : null
  const formalizationColor = isFormalized ? '#1d6664' : '#a8740e'

  const locationNames = useMemo(() => resolveUbigeoNames(right.ubigeoCode), [right.ubigeoCode])
  const polygonPositions = useMemo(() => parseGeoJsonPositions(right.geoJson), [right.geoJson])
  const officialPositions = useMemo(() => parseGeoJsonPositions(right.officialGeoJson), [right.officialGeoJson])
  // El encuadre abarca las dos formas, porque el derecho suele ser bastante mayor que la
  // concesion y encuadrar solo una dejaria la otra fuera de pantalla.
  //
  // Salvo cuando la concesion NO cae dentro del derecho: ahi las dos pueden estar a
  // cientos de kilometros (en dev hay un caso a 987 km) y el encuadre conjunto muestra
  // medio pais con las dos formas invisibles. En ese caso manda la concesion, que es la
  // geometria operativa; que esten lejos ya lo dice el aviso del panel.
  const encuadre = useMemo(() => {
    // Solo si de verdad estan lejos (> 50 km entre centros): una concesion que apenas
    // asoma del derecho debe verse junto a el.
    if (right.concessionWithinOfficial === false && polygonPositions.length >= 3 && officialPositions.length >= 3) {
      const c = (pts: LL[]) => ({ lat: pts.reduce((a, p) => a + p.lat, 0) / pts.length, lng: pts.reduce((a, p) => a + p.lng, 0) / pts.length })
      const a1 = c(polygonPositions), b1 = c(officialPositions)
      const km = Math.hypot((a1.lat - b1.lat) * 111, (a1.lng - b1.lng) * 111 * Math.cos((a1.lat * Math.PI) / 180))
      if (km > 50) return polygonPositions
    }
    return [...polygonPositions, ...officialPositions]
  }, [polygonPositions, officialPositions, right.concessionWithinOfficial])

  const hasGeoreference = right.hasBoundary
  const hasComponents = components.length > 0
  const completedCount = [hasGeoreference, hasComponents].filter(Boolean).length
  const progreso = Math.round((completedCount / 2) * 100)

  const handleSavePolygon = async (vertices: { northing: number; easting: number }[], utmZone: number) => {
    await onSavePolygon(right.id, vertices, utmZone)
    onUpdated?.()
    // Re-fetch the screening result so the warning card reflects the new polygon
    // immediately. We could avoid this roundtrip by piping updatePolygon's body
    // through onSavePolygon, but that ripples through 3 layers of props for one
    // GET — pay the extra request and keep the surface area small.
    if (companyId) {
      try {
        const check = await proveedoresService.getRestrictionCheck(companyId, right.id)
        setRestrictionCheck(check)
      } catch { /* leave stale */ }
    }
  }

  const handleAddComponent = async (addData: { componentTypeId: string; name?: string; northing: number; easting: number }) => {
    const result = await onAddComponent(right.id, addData)
    onUpdated?.()
    return result
  }

  const handleRemoveComponent = async (componentId: string) => {
    await onRemoveComponent(right.id, componentId)
    onUpdated?.()
  }

  const handleComponentsClick = () => {
    if (!hasGeoreference) {
      toaster.error({ description: t('shared.miningRightCard.componentsRequireGeo') })
      return
    }
    setComponentsOpen(true)
  }

  return (
    <>
      <Box bg={c.bgHover} borderRadius="2xl" p={{ base: 5, md: 6 }}>
        {/* ── Top: Code + Badge + Name + Location + Mini Map ── */}
        <Flex gap={4} mb={5}>
          <Box flex={1} minW={0}>
            <Flex gap={3} alignItems="center" mb={2} flexWrap="wrap">
              <Text tid="shared.miningRightCard.codeLabel" values={{ code: right.uniqueCode }} fontSize="12px" fontWeight="600" color="#1d6664" fontFamily="monospace" letterSpacing="0.03em" />
              <Box
                px="10px" py="2px" borderRadius="full" fontSize="11px" fontWeight="700"
                textTransform="uppercase" letterSpacing="0.05em"
                border="1.5px solid" borderColor={statusBorderColor} color={statusTextColor} bg="transparent"
              >
                {statusLabel}
              </Box>
              {formalizationLabel && (
                <Box
                  px="10px" py="2px" borderRadius="full" fontSize="11px" fontWeight="700"
                  textTransform="uppercase" letterSpacing="0.05em"
                  border="1.5px solid" borderColor={formalizationColor} color={formalizationColor} bg="transparent"
                >
                  {formalizationLabel}
                </Box>
              )}
            </Flex>
            <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800" color="#2a2622" lineHeight="1.15" mb={1}>
              {right.name}
            </Text>
            {locationNames.length > 0 && (
              <Text fontSize="sm" color="#857b71">{locationNames.join(' / ')}</Text>
            )}
          </Box>

          {/* Mini map - only when georef exists */}
          {(polygonPositions.length >= 3 || officialPositions.length >= 3) && (
            <Flex flexDirection="column" alignItems="center" gap={1} flexShrink={0} display={{ base: 'none', md: 'flex' }}
              cursor="pointer" onClick={() => setMapOpen(true)}>
              <Box
                w="90px" h="90px" borderRadius="xl" overflow="hidden"
                border="1px solid #c8e6c9"
              >
                <KMap
                  defaultCenter={{ lat: -12, lng: -77 }}
                  defaultZoom={6}
                  mapTypeId="hybrid"
                  disableDefaultUI
                  gestureHandling="none"
                >
                  {officialPositions.length >= 3 && (
                    <PolygonOverlay paths={officialPositions} fillOpacity={0.06} color={COLOR_DERECHO} zIndex={1} />
                  )}
                  {polygonPositions.length >= 3 && (
                    <PolygonOverlay paths={polygonPositions} fillOpacity={0.25} zIndex={2} />
                  )}
                  <FitBounds points={encuadre} padding={10} maxZoom={15} />
                </KMap>
              </Box>
              <Text tid="shared.miningRightCard.viewMap" fontSize="10px" fontWeight="600" color="#1d6664" _hover={{ textDecoration: 'underline' }} />
            </Flex>
          )}
        </Flex>

        {/* ── Bottom: Info adicional sub-card ── */}
        <Box bg="white" borderRadius="xl" border="1px solid #e8dfd1" px={{ base: 4, md: 5 }} py={4}>
          <Flex alignItems="center" justifyContent="space-between" gap={4} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
            <Box flex={1}>
              <Text tid="shared.miningRightCard.additionalInfo" fontSize="11px" fontWeight="700" color="#b5aa9d" textTransform="uppercase" letterSpacing="0.08em" mb={3} />
              <Flex gap={{ base: 4, md: 8 }} flexWrap="wrap">
                {/* Georreferenciacion */}
                <Flex alignItems="center" gap="10px">
                  <Box flexShrink={0}>
                    {hasGeoreference ? (
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="#b4532a">
                        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                      </svg>
                    ) : (
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#b5aa9d" strokeWidth="2">
                        <circle cx="12" cy="12" r="9" />
                      </svg>
                    )}
                  </Box>
                  <Box>
                    <Flex alignItems="center" gap={1.5}>
                      <Text tid="shared.miningRightCard.georeferencing" fontSize="13px" fontWeight="600" color="#2d3748" lineHeight="1.3" />
                      {/* De donde salio la geometria. No es cosmetico: de ella dependen el
                          screening de zonas y la ubicacion de los componentes, y un poligono
                          dibujado a mano puede caer en el departamento equivocado. */}
                      {hasGeoreference && right.boundaryFromIngemmet === true && (
                        <Box px="6px" py="1px" borderRadius="full" bg="#c6f6d5" color="#22543d"
                          fontSize="9px" fontWeight="800" letterSpacing="0.05em" title="Poligono oficial de INGEMMET">
                          OFICIAL
                        </Box>
                      )}
                      {hasGeoreference && right.boundaryFromIngemmet === false && (
                        <Box px="6px" py="1px" borderRadius="full" bg="#edf2f7" color="#4a5568"
                          fontSize="9px" fontWeight="800" letterSpacing="0.05em" title="Poligono dibujado manualmente">
                          MANUAL
                        </Box>
                      )}
                    </Flex>
                    <Text
                      fontSize="12px" fontWeight="700" color="#1d6664" lineHeight="1.3"
                      cursor="pointer" _hover={{ textDecoration: 'underline' }}
                      onClick={() => setGeorefOpen(true)}
                    >
                      {hasGeoreference ? t('shared.miningRightCard.edit') : t('shared.miningRightCard.add')}
                    </Text>
                  </Box>
                </Flex>

                {/* Componentes */}
                <Flex alignItems="center" gap="10px" opacity={hasGeoreference ? 1 : 0.4}>
                  <Box flexShrink={0}>
                    {hasComponents ? (
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="#b4532a">
                        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                      </svg>
                    ) : (
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#b5aa9d" strokeWidth="2">
                        <circle cx="12" cy="12" r="9" />
                      </svg>
                    )}
                  </Box>
                  <Box>
                    <Text tid="shared.miningRightCard.components" fontSize="13px" fontWeight="600" color="#2d3748" lineHeight="1.3" />
                    <Text
                      fontSize="12px" fontWeight="700" color={hasGeoreference ? '#1d6664' : '#b5aa9d'} lineHeight="1.3"
                      cursor={hasGeoreference ? 'pointer' : 'not-allowed'}
                      _hover={hasGeoreference ? { textDecoration: 'underline' } : {}}
                      onClick={handleComponentsClick}
                    >
                      {hasComponents ? t('shared.miningRightCard.edit') : t('shared.miningRightCard.add')}
                    </Text>
                  </Box>
                </Flex>
              </Flex>
            </Box>

            {/* Progreso */}
            <Box
              textAlign="right" flexShrink={0}
              pl={{ base: 0, md: 6 }}
              borderLeft={{ base: 'none', md: '1px solid #e8dfd1' }}
            >
              <Text tid="shared.miningRightCard.progress" fontSize="11px" fontWeight="700" color="#b5aa9d" textTransform="uppercase" letterSpacing="0.08em" mb={1} />
              <Text fontSize="3xl" fontWeight="800" color={progreso === 100 ? '#1d6664' : '#a8740e'} lineHeight="1">
                {progreso}%
              </Text>
            </Box>
          </Flex>
        </Box>

        {/* Restricted zones screening — informational, never blocks. */}
        <RestrictionWarningCard
          check={restrictionCheck}
          onRecheck={companyId ? handleRecheck : undefined}
          rechecking={rechecking}
        />

        {/* Verificacion contra el registro oficial. Es un eje distinto del progreso de
            registro que esta arriba: ese mide lo que le falta cargar al usuario, esto es
            lo que dice INGEMMET. */}
        <IngemmetOfficialPanel
          right={right}
          onImport={companyId ? handleIngemmetImport : undefined}
          importing={importingIngemmet}
          onDescargarExpediente={companyId ? handleDescargarExpediente : undefined}
          progresoExpediente={progresoExpediente}
        />
      </Box>

      {/* Modals */}
      <GeoreferenceModal
        open={georefOpen}
        onClose={() => setGeorefOpen(false)}
        miningRightName={right.name}
        ubigeoCode={right.ubigeoCode}
        existingGeoJson={right.geoJson}
          officialGeoJson={right.officialGeoJson}
        existingUtmZone={right.utmZone}
        onSave={handleSavePolygon}
      />
      {/* Map fullscreen modal */}
      <Modal
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        title={right.name}
        subtitle={locationNames.join(' / ')}
        size="lg"
      >
        {/* Sin leyenda las dos formas no se distinguen: una es el derecho minero completo
            y la otra el area que trabaja este minero dentro de el. */}
        <Flex gap={4} mb={2} flexWrap="wrap" alignItems="center">
          <Flex gap={1.5} alignItems="center">
            <Box w="14px" h="14px" borderRadius="sm" border="2px solid #1d6664" bg="rgba(29,102,100,0.2)" />
            <Text fontSize="11px" color="#4a5568" fontWeight="600">Concesión del minero</Text>
          </Flex>
          <Flex gap={1.5} alignItems="center">
            <Box w="14px" h="14px" borderRadius="sm" border={`2px solid ${COLOR_DERECHO}`} bg="rgba(212,154,42,0.06)" />
            <Text fontSize="11px" color="#4a5568" fontWeight="600">Derecho minero (INGEMMET)</Text>
          </Flex>
          {officialPositions.length === 0 && (
            <Text fontSize="11px" color="#a0aec0">Sin polígono del derecho minero: consulta INGEMMET.</Text>
          )}
        </Flex>
        <Box h="calc(80vh - 180px)" minH="400px">
          {mapOpen && (
            <KMap defaultCenter={{ lat: -12, lng: -77 }} defaultZoom={6} mapTypeId="hybrid">
              {officialPositions.length >= 3 && (
                <PolygonOverlay paths={officialPositions} fillOpacity={0.05} color={COLOR_DERECHO} strokeWeight={3} zIndex={1} />
              )}
              {polygonPositions.length >= 3 && (
                <PolygonOverlay paths={polygonPositions} fillOpacity={0.2} zIndex={2} />
              )}
              <FitBounds points={encuadre} padding={10} maxZoom={15} />
            </KMap>
          )}
        </Box>
      </Modal>

      <ComponentsModal
        open={componentsOpen}
        onClose={() => setComponentsOpen(false)}
        miningRightName={right.name}
        utmZone={utmZone}
        polygonGeoJson={right.geoJson}
        components={components}
        onAdd={handleAddComponent}
        onRemove={handleRemoveComponent}
      />
    </>
  )
}

export default MiningRightCard
